import { LegalTocSection } from '../../core/components/legal-toc/legal-toc.component';
import { ANNEX_PRODUCTS, AnnexProduct, PRODUCT_LABELS, PRODUCT_TOC_ORDER, ResolvedProductDoc } from './product-legal.registry';

export interface ProductTocOptions {
    /** i18n key of the heading over the group-wide base text ('Allgemeine Geschäftsbedingungen', …). */
    generalKey: string;
    /** The numbered base sections of the page. */
    baseSections: readonly LegalTocSection[];
    /** Resolved standalone product documents (ScreenDeck, Chroniq). */
    docs: readonly ResolvedProductDoc[];
    /** DOM id of a standalone product document's section; shared with the template. */
    sectionId: (product: string, num: string) => string;
    /** i18n key of the short annex card's heading (TableStack, MenuLink). */
    annexTitleKey: (product: AnnexProduct) => string;
}

/**
 * Section index of a legal page, divided by product: first the group-wide base text, then one block
 * per product in PRODUCT_TOC_ORDER. A standalone product contributes one entry per section of its
 * document, an annex product (short note) a single entry. Each block's heading jumps to the
 * product's header card, whose DOM id is the product key.
 */
export function buildProductToc(options: ProductTocOptions): LegalTocSection[] {
    const general = options.baseSections.map(section => ({
        ...section,
        groupKey: options.generalKey,
        groupTarget: options.baseSections[0]?.id,
    }));

    const products = PRODUCT_TOC_ORDER.flatMap((key): LegalTocSection[] => {
        const group = PRODUCT_LABELS[key];

        const document = options.docs.find(d => d.key === key);
        if (document) {
            return document.doc.sections.map(section => ({
                id: options.sectionId(key, section.num),
                num: section.num,
                title: section.title,
                group,
                groupTarget: key,
            }));
        }

        if ((ANNEX_PRODUCTS as readonly string[]).includes(key)) {
            return [{
                id: key,
                num: '·',
                titleKey: options.annexTitleKey(key as AnnexProduct),
                group,
                groupTarget: key,
            }];
        }

        return [];
    });

    return [...general, ...products];
}
